import { useRef, useEffect } from 'react'
import { Modal } from 'react-daisyui'
import { AlbumFile } from '@/models/albumFile.ts'
import { FileAction } from '@/components/Files/FileHoverOverlay.tsx'
import { ComponentType } from 'react'

// Generic action used by SimpleActionsModal
export interface SimpleAction {
  label: string
  icon: ComponentType<{ size?: number | string; className?: string }>
  onClick: () => void
  className?: string
}

// --- Generic actions modal (reusable for any context) ---

export function SimpleActionsModal({ open, title, actions, onClose }: {
  open: boolean
  title: string
  actions: SimpleAction[]
  onClose: () => void
}) {
  const modalRef = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    if (open) {
      modalRef.current?.showModal()
    } else {
      modalRef.current?.close()
    }
  }, [open])

  return (
     // @ts-ignore onClose works at runtime via native dialog
    <Modal ref={modalRef} className="bg-neutral" onClose={onClose}>
      <Modal.Header>
        <span className="text-sm font-bold truncate">{title}</span>
        <form method="dialog">
          <button className="btn btn-sm btn-circle btn-ghost absolute right-5 top-5">✕</button>
        </form>
      </Modal.Header>
      <Modal.Body>
        <div className="flex flex-col gap-1">
          {actions.map((action) => (
            <button
              key={action.label}
              className={`btn btn-ghost justify-start gap-3 ${action.className || ''}`}
              onClick={() => { onClose(); action.onClick() }}
            >
              <action.icon size={20} />
              <span>{action.label}</span>
            </button>
          ))}
        </div>
      </Modal.Body>
    </Modal>
  )
}

// --- File-specific actions modal (for gallery long-press) ---

interface FileActionsModalProps {
  file: AlbumFile | null
  actions: FileAction[]
  onClose: () => void
}

export default function FileActionsModal({ file, actions, onClose }: FileActionsModalProps) {
  const simpleActions: SimpleAction[] = file
    ? actions.map(a => ({ label: a.label, icon: a.icon, onClick: () => a.onClick(file), className: a.className }))
    : []

  return (
    <SimpleActionsModal
      open={!!file}
      title={file?.file_name || ''}
      actions={simpleActions}
      onClose={onClose}
    />
  )
}
